import { Link } from "react-router-dom";
import { Plus } from "lucide-react";
import { useAuthStore } from "@/store/auth";
import { useClients } from "@/features/clients/hooks/useClients";
import { PageLoader } from "@/components/ui/Spinner";
import { Badge } from "@/components/ui/Badge";
import type { Client, ClientProjectBrief } from "@/features/clients/types";

type Row = { client: Client; project: ClientProjectBrief };

// Projects of one client are grouped by branch (then sub-branch, then short name);
// projects without a branch go last.
function compareProjects(a: ClientProjectBrief, b: ClientProjectBrief): number {
  const branchA = a.client_branch_detail?.display_name;
  const branchB = b.client_branch_detail?.display_name;
  if (branchA !== branchB) {
    if (!branchA) return 1;
    if (!branchB) return -1;
    return branchA.localeCompare(branchB, "pl");
  }
  const subA = a.client_sub_branch_detail?.display_name ?? "";
  const subB = b.client_sub_branch_detail?.display_name ?? "";
  return subA.localeCompare(subB, "pl") || a.short_name.localeCompare(b.short_name, "pl");
}

function buildRows(clients: Client[]): Row[] {
  return clients
    .slice()
    .sort((a, b) => a.name.localeCompare(b.name, "pl"))
    .flatMap((c): Row[] => {
      if (c.projects_brief.length === 0) {
        return [{ client: c, project: { id: "", name: "", short_name: "", status: "", client_branch_detail: null, client_sub_branch_detail: null } }];
      }
      return c.projects_brief
        .slice()
        .sort(compareProjects)
        .map((p) => ({ client: c, project: p }));
    });
}

export function ClientsListPage() {
  const user = useAuthStore((s) => s.user);
  const isDirector = user?.role === "director";

  const { data: clients = [], isLoading } = useClients(
    user?.is_ceo || user?.role === "coordinator" ||
    (user?.role === "director" && !!user?.is_technical_director),
  );
  const rows = buildRows(clients);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Klienci</h1>
          <div className="w-8 h-0.5 bg-[var(--color-primary)] mt-1 mb-6" />
        </div>
        {isDirector && (
          <Link
            to="/clients/new"
            className="inline-flex items-center gap-1.5 rounded-md bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--color-primary-hover)]"
          >
            <Plus size={14} /> Nowy klient
          </Link>
        )}
      </div>

      {isLoading ? (
        <PageLoader />
      ) : rows.length === 0 ? (
        <p className="text-gray-400 dark:text-gray-500 text-sm">Brak klientów.</p>
      ) : (
        <>
          {/* Mobile card list */}
          <div className="sm:hidden flex flex-col divide-y divide-gray-100 dark:divide-gray-700 bg-white dark:bg-gray-800">
            {rows.map((row, idx) => {
              const { client: c, project: p } = row;
              const prevClient = idx > 0 ? rows[idx - 1].client : null;
              const isFirstOfClient = prevClient?.id !== c.id;
              return (
                <div
                  key={p.id ? `${c.id}-${p.id}` : c.id}
                  className={`px-4 py-3 ${isFirstOfClient && idx > 0 ? "border-t-2 border-gray-200 dark:border-gray-600" : ""}`}
                >
                  {isFirstOfClient && (
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <Link to={`/clients/${c.id}`} className="font-medium text-[var(--color-primary)] hover:underline">
                        {c.name}
                        {c.short_name && <span className="ml-1.5 text-xs text-gray-400 dark:text-gray-500">{c.short_name}</span>}
                      </Link>
                      <Badge color={c.is_active ? "green" : "gray"}>
                        {c.is_active ? "Aktywny" : "Nieaktywny"}
                      </Badge>
                    </div>
                  )}
                  {p.id && (
                    <div className={`${isFirstOfClient ? "" : "pl-3 border-l-2 border-gray-100 dark:border-gray-700"} flex items-start justify-between gap-2`}>
                      <div className="min-w-0 flex-1">
                        <Link to={`/projects/${p.id}`} className="text-xs hover:underline group">
                          <span className="font-mono font-semibold text-[var(--color-primary)]">{p.short_name}</span>
                          <span className="text-gray-600 dark:text-gray-400"> — {p.name}</span>
                        </Link>
                        {(p.client_branch_detail || p.client_sub_branch_detail) && (
                          <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                            {[p.client_branch_detail?.display_name, p.client_sub_branch_detail?.display_name].filter(Boolean).join(" · ")}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                  {!p.id && isFirstOfClient && (
                    <p className="text-xs text-gray-300 dark:text-gray-600">Brak przedsięwzięć</p>
                  )}
                </div>
              );
            })}
          </div>
          {/* Desktop table */}
          <div className="hidden sm:block bg-white dark:bg-gray-800 overflow-x-auto">
          <table className="w-full text-xs min-w-[700px]">
            <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
              <tr>
                <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Klient</th>
                <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">NIP</th>
                <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Oddział</th>
                <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Pododdział</th>
                <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Przedsięwzięcie</th>
                <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {rows.map((row, idx) => {
                const { client: c, project: p } = row;
                const prevClient = idx > 0 ? rows[idx - 1].client : null;
                const isFirstOfClient = prevClient?.id !== c.id;
                // The same branch as the row above (within one client) is shown as ↳,
                // like the client column.
                const branch = p.client_branch_detail;
                const repeatsBranch =
                  !isFirstOfClient && !!branch && rows[idx - 1].project.client_branch_detail?.id === branch.id;

                return (
                  <tr
                    key={p.id ? `${c.id}-${p.id}` : c.id}
                    className={[
                      "hover:bg-gray-50 dark:hover:bg-gray-700/50",
                      isFirstOfClient && idx > 0 ? "border-t-2 border-gray-200 dark:border-gray-600" : "",
                    ].join(" ")}
                  >
                    <td className="px-4 py-3 whitespace-nowrap">
                      {isFirstOfClient ? (
                        <div>
                          <Link
                            to={`/clients/${c.id}`}
                            className="font-medium text-[var(--color-primary)] hover:underline"
                          >
                            {c.name}
                          </Link>
                          {c.short_name && (
                            <span className="ml-2 text-xs text-gray-400 dark:text-gray-500">{c.short_name}</span>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-300 dark:text-gray-600 text-xs pl-2">↳</span>
                      )}
                    </td>

                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400 font-mono text-xs whitespace-nowrap">
                      {isFirstOfClient ? (c.nip || <span className="text-gray-300 dark:text-gray-600">—</span>) : ""}
                    </td>

                    <td className="px-4 py-3 text-xs text-gray-600 dark:text-gray-400">
                      {repeatsBranch ? (
                        <span className="text-gray-300 dark:text-gray-600 text-xs pl-2" title={branch.display_name}>↳</span>
                      ) : branch ? (
                        branch.display_name
                      ) : (
                        <span className="text-gray-300 dark:text-gray-600">—</span>
                      )}
                    </td>

                    <td className="px-4 py-3 text-xs text-gray-600 dark:text-gray-400">
                      {p.client_sub_branch_detail ? (
                        p.client_sub_branch_detail.display_name
                      ) : (
                        <span className="text-gray-300 dark:text-gray-600">—</span>
                      )}
                    </td>

                    <td className="px-4 py-3">
                      {p.id ? (
                        <Link
                          to={`/projects/${p.id}`}
                          className="flex gap-1.5 items-baseline text-xs hover:underline group"
                        >
                          <span className="font-mono font-semibold text-[var(--color-primary)] shrink-0">{p.short_name}</span>
                          <span className="text-gray-600 dark:text-gray-400 group-hover:text-[var(--color-primary)]">— {p.name}</span>
                        </Link>
                      ) : (
                        <span className="text-gray-300 dark:text-gray-600 text-xs">—</span>
                      )}
                    </td>

                    <td className="px-4 py-3">
                      {isFirstOfClient && (
                        <Badge color={c.is_active ? "green" : "gray"}>
                          {c.is_active ? "Aktywny" : "Nieaktywny"}
                        </Badge>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </>
      )}
    </div>
  );
}
